import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../utils/api'
import {
  readPersistedUser,
  readPersistedTokens,
  establishAuthSession,
  saveUserAndTokensToActiveStorage,
  clearAllAuthStorage,
  type AuthTokens,
} from '../utils/authStorage'
import { User } from '../types'

export type { AuthTokens }

export interface LoginResponse {
  user: User
  tokens: AuthTokens
}

export interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isAuthenticated: boolean
  isLoading: boolean
}

function loadInitialAuth(): { user: User | null; tokens: AuthTokens | null } {
  return {
    user: readPersistedUser(),
    tokens: readPersistedTokens(),
  }
}

export const useAuth = () => {
  const initial = loadInitialAuth()
  const [user, setUser] = useState<User | null>(initial.user)
  const [tokens, setTokens] = useState<AuthTokens | null>(initial.tokens)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user && !!tokens?.accessToken

  const userRef = useRef(user)
  userRef.current = user

  const clearAuthState = useCallback(() => {
    clearAllAuthStorage()
    setUser(null)
    setTokens(null)
    delete api.defaults.headers.common['Authorization']
  }, [])

  useEffect(() => {
    if (user?.id) {
      window.dispatchEvent(new CustomEvent('userStateChanged', {
        detail: { user: user.email, role: user.role, userId: user.id }
      }))
    }
  }, [user?.id, user?.email, user?.role])

  const isTokenExpired = useCallback(() => {
    if (!tokens?.expiresIn) return true
    return Date.now() >= tokens.expiresIn
  }, [tokens])

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!tokens?.refreshToken) return false

    try {
      const response = await api.post<{ tokens: AuthTokens }>('/auth/refresh', {
        refreshToken: tokens.refreshToken
      })
      const newTokens = response.data.tokens

      setTokens(newTokens)
      const u = userRef.current
      if (u) saveUserAndTokensToActiveStorage(u, newTokens)

      api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.accessToken}`

      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      clearAuthState()
      return false
    }
  }, [tokens, clearAuthState])

  const tokensRef = useRef(tokens)
  tokensRef.current = tokens

  useEffect(() => {
    let isMounted = true
    const initializeAuth = async () => {
      try {
        if (tokensRef.current?.accessToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${tokensRef.current.accessToken}`
          if (isTokenExpired()) {
            const refreshed = await refreshToken()
            if (isMounted && !refreshed) {
              clearAuthState()
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
        if (isMounted) clearAuthState()
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    initializeAuth()
    return () => { isMounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          const refreshed = await refreshToken()
          if (refreshed) {
            return api(originalRequest)
          }
        }

        return Promise.reject(error)
      }
    )

    return () => {
      api.interceptors.response.eject(interceptor)
    }
  }, [refreshToken])

  const login = async (
    email: string,
    password: string,
    rememberMe = true
  ): Promise<boolean> => {
    try {
      setIsLoading(true)

      clearAuthState()

      const apiResponse = await api.post('/auth/login', {
        email,
        password
      })
      const response: LoginResponse = apiResponse.data.data

      const { user: userData, tokens: authTokens } = response

      establishAuthSession(userData, authTokens, rememberMe)
      setUser(userData)
      setTokens(authTokens)

      api.defaults.headers.common['Authorization'] = `Bearer ${authTokens.accessToken}`

      localStorage.removeItem('switchedRole')
      localStorage.removeItem('isRoleSwitched')
      sessionStorage.removeItem('switchedRole')
      sessionStorage.removeItem('isRoleSwitched')

      return true
    } catch (error: any) {
      console.error('Login failed:', error)

      if (error.response?.status === 401) {
        throw new Error('Invalid email or password')
      } else if (error.response?.status === 422) {
        throw new Error('Please check your input and try again')
      } else {
        throw new Error(error.message || 'An error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      if (tokens?.accessToken) {
        await api.post('/auth/logout', {
          refreshToken: tokens.refreshToken
        })
      }
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      clearAuthState()
    }
  }

  const updateUser = (updates: Partial<User>) => {
    if (user && tokens) {
      const next = { ...user, ...updates }
      setUser(next)
      saveUserAndTokensToActiveStorage(next, tokens)
    }
  }

  const forgotPassword = async (email: string): Promise<boolean> => {
    try {
      await api.post('/auth/forgot-password', { email })
      return true
    } catch (error: any) {
      console.error('Forgot password failed:', error)
      if (error.response?.status === 404) {
        throw new Error('No account found with this email address')
      } else {
        throw new Error(error.message || 'An error occurred. Please try again.')
      }
    }
  }

  const resetPassword = async (token: string, password: string): Promise<boolean> => {
    try {
      await api.post('/auth/reset-password', { token, password })
      return true
    } catch (error: any) {
      console.error('Reset password failed:', error)
      if (error.response?.status === 400) {
        throw new Error('Invalid or expired reset token')
      } else {
        throw new Error(error.message || 'An error occurred. Please try again.')
      }
    }
  }

  const setAuth = (nextUser: User, nextTokens: AuthTokens) => {
    establishAuthSession(nextUser, nextTokens, true)
    setUser(nextUser)
    setTokens(nextTokens)
    api.defaults.headers.common['Authorization'] = `Bearer ${nextTokens.accessToken}`
  }

  return {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser,
    forgotPassword,
    resetPassword,
    refreshToken,
    setAuth
  }
}
